import React, { useEffect, useState } from "react";
import { useAuthContext } from "../../../context/AuthContext";
import { Link, useHistory } from "react-router-dom";
import LoadingPageSm from "../../CommonComponent/LoadingPageSm";
import { AlertInfo } from "../../Alerts/Alert";
import ProfileImageMd from "../../CommonComponent/ProfileImageMd";
import { BadgeInfo } from "../../Badge/Badge";
import { Button } from "react-bootstrap";
import getMyResourceListing from "../../../apis/getMyResourceListing";
import updateIsAdvertised from "../../../apis/updateIsAdvertised";
import updateSelfService from "../../../apis/updateSelfService";
import saveResourceAvailability from "../../../apis/saveResourceAvailability";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapSigns } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import swalWithBootstrapButtons from "sweetalert2-react-content";
import FullCircleChart from "../../Charts/FullCircleChart";
import BlockHeader from "../../Headers/BlockHeader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../assets/css/availability-modal.css"; // adjust depth based on your folder level

/* ── Availability Modal ── */
const AvailabilityModal = ({ item, currentUserSlug, onClose, onUpdate }) => {
  const originalValue = parseInt(item.availability) || 0;
  const [selected, setSelected] = useState(originalValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setSelected(originalValue); // reset on close
    onClose();
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const res = await saveResourceAvailability({
        current_user_slug: currentUserSlug,
        resourceSlug: item.user_slug,
        availability: selected,
      });

      if (res?.data?.status === 1) {
        toast.success(res.data.message);
        onUpdate(item, selected);
        onClose();
      } else {
        toast.warning(res?.data?.message || "Something went wrong.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="avail-overlay" onClick={handleClose}>
      <div className="avail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="avail-modal-header">
          <h2 className="avail-modal-title">Edit Your Weekly Availability</h2>
        </div>

        <div className="avail-modal-body">
          <p className="avail-modal-sub">I'm available up to</p>
          <div className="avail-grid">
            {[40, 30, 20, 10, 5, 0].map((val) => (
              <button
                key={val}
                className={`avail-option${selected === val ? " selected" : ""}${val === 0 ? " not-available" : ""}`}
                onClick={() => setSelected(val)}
                disabled={isSubmitting}
              >
                {val > 0 ? `${val} hrs/week` : "Not Currently Available"}
              </button>
            ))}
          </div>
        </div>

        <div className="avail-modal-footer">
          <button
            className="avail-btn-close"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            className="avail-btn-save"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ── */
const ResourceListingResultBlock = () => {
  let history = useHistory();
  const { userDetails } = useAuthContext();
  const MySwal = swalWithBootstrapButtons(Swal);
  const firmType = JSON.parse(userDetails).firm_details?.firm_type;
  const firmId = JSON.parse(userDetails).firm_details?.firm_id;
  const user_id = JSON.parse(userDetails).user_id;
  const user_slug = JSON.parse(userDetails).user_slug;
  const user_primary_role = JSON.parse(userDetails).user_primary_role;
  const current_user_slug = JSON.parse(userDetails).user_slug;

  const [isLoading, setIsLoading] = useState(true);
  const [resourceListing, setResourceListing] = useState(false);
  const [hasResult, setHasResult] = useState(false);
  const [emptyResult, setEmptyResult] = useState("");
  const [filterText, setFilterText] = useState("");
  const [favList, setFavList] = useState(false);
  const [availModal, setAvailModal] = useState(null);

  useEffect(() => {
    if (user_slug !== undefined) {
      const timeoutId = setTimeout(
        () => [setIsLoading(true), getResourceListing()],
        500,
      );
      return () => clearTimeout(timeoutId);
    }
  }, [filterText, user_slug]);

  const getResourceListing = () => {
    Promise.all([getMyResourceListing(user_slug, filterText)])
      .then(async ([data]) => {
        if (data?.data?.status === 1) {
          if (data?.data?.resourceList) {
            setResourceListing(data?.data?.resourceList);
            if (data?.data?.fav_list) setFavList(data?.data?.fav_list);
            setIsLoading(false);
            setHasResult(true);
          } else {
            setIsLoading(false);
            setEmptyResult("No resource result found for your account access.");
          }
        } else {
          setIsLoading(false);
          setHasResult(false);
          setEmptyResult(data?.data?.message);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setEmptyResult(false);
        console.log(err);
      });
  };

  const updateAdvertised = (advertised, resourceSlug, index) => {
    Promise.all([updateIsAdvertised(user_slug, advertised, resourceSlug)])
      .then(async ([data]) => {
        if (data?.data?.status === 1) {
          let updatedResourceList = [...resourceListing];
          updatedResourceList[index].is_advertised =
            advertised === "0" ? "1" : "0";
          setResourceListing(updatedResourceList);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setEmptyResult(false);
        console.log(err);
      });
  };

  const updateSelfServiceEdit = (resourceSlug, index, name) => {
    MySwal.fire({
      title: "Are you sure?",
      text: `You want to update self-service access for ${name}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      confirmButtonColor: "var(--danger)",
      cancelButtonColor: "var(--black)",
    }).then(async (result) => {
      if (result.isConfirmed) processUpdateSelfService(resourceSlug, index);
    });
  };

  const processUpdateSelfService = (resourceSlug, index) => {
    Promise.all([updateSelfService(user_slug, resourceSlug)])
      .then(async ([data]) => {
        if (data?.data?.status === 1) {
          let updatedResourceList = [...resourceListing];
          updatedResourceList[index] = data?.data?.resource;
          setResourceListing(updatedResourceList);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setEmptyResult(false);
        console.log(err);
      });
  };

  /* Update local list after successful save */
  const handleAvailabilityUpdate = (item, newValue) => {
    const updatedList = resourceListing.map((r) =>
      r.user_slug === item.user_slug
        ? { ...r, availability: String(newValue) }
        : r,
    );
    setResourceListing(updatedList);
  };

  const navigateTpProfile = (path) => {
    history.push(path);
  };

  const checkSelfServices = (firmIds) => {
    if (firmIds !== null) {
      return firmIds.split(", ").indexOf(firmId) !== -1;
    }
    return false;
  };

  const displayUserProfileRole = (user_profile_role) =>
    user_profile_role !== null ? (
      <span className="text-link text-sm">{user_profile_role}</span>
    ) : (
      <span className="text-danger text-sm">Not available</span>
    );

  const displayName = (name) => {
    if (name !== null) {
      return name.length > 20 ? (
        <span className="fw-medium text-dark">
          {name.substring(0, 20) + "..."}
        </span>
      ) : (
        <span className="fw-medium text-dark">{name}</span>
      );
    }
    return <BadgeInfo title={"Not available"} />;
  };

  const displayList = () => (
    <div className="card mb-4">
      <div className="card-body">
        <BlockHeader
          title={"Available Resources"}
          subText="Manage visibility, access, and availability of your team"
        />
        <div className="table-wrapper">
          <table className="table table-responsive">
            <thead>
              <tr>
                <th>Name & Role</th>
                <th>Visible to Clients</th>
                <th>Self-Service</th>
                <th>Location</th>
                <th>Availability</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {resourceListing.map((item, index) => (
                <tr key={index}>
                  <td className="d-flex">
                    <Link to={`resources/details/${item.user_slug}`}>
                      <ProfileImageMd imgSrc={item.profile_image_path} />
                    </Link>
                    <div className="d-block ms-2">
                      <Link to={`resources/details/${item.user_slug}`}>
                        {displayName(item.resource_name)}
                      </Link>
                      <div className="d-block">
                        {displayUserProfileRole(item.user_profile_role)}
                      </div>
                    </div>
                  </td>

                  <td>
                    {user_primary_role === "2" ? (
                      <div className="form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          onChange={() =>
                            updateAdvertised(
                              item.is_advertised,
                              item.user_slug,
                              index,
                            )
                          }
                          checked={item.is_advertised === "1"}
                        />
                      </div>
                    ) : (
                      <span
                        className={`fw-bold ${item.is_advertised === "1" ? "text-success" : "text-danger"}`}
                      >
                        {item.is_advertised === "1" ? "Yes" : "No"}
                      </span>
                    )}
                  </td>

                  <td>
                    {user_primary_role === "2" ? (
                      <div className="form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          onChange={() =>
                            updateSelfServiceEdit(
                              item.user_slug,
                              index,
                              item.resource_name,
                            )
                          }
                          checked={checkSelfServices(
                            item.firm_ids_self_services,
                          )}
                        />
                      </div>
                    ) : (
                      <span
                        className={`fw-bold ${checkSelfServices(item.firm_ids_self_services) ? "text-success" : "text-danger"}`}
                      >
                        {checkSelfServices(item.firm_ids_self_services)
                          ? "Yes"
                          : "No"}
                      </span>
                    )}
                  </td>

                  <td>
                    <FontAwesomeIcon
                      icon={faMapSigns}
                      className="me-1 text-primary"
                    />
                    <span className="text-muted text-sm">
                      {[item.city_name, item.state_name, item.country_name]
                        .filter(Boolean)
                        .join(", ") || "Location not available"}
                    </span>
                  </td>

                  {/* Availability — click to edit */}
                  <td className="text-center">
                    <div
                      className="avail-trigger"
                      onClick={() => setAvailModal(item)}
                      title="Edit availability"
                    >
                      <FullCircleChart
                        value={parseInt(item.availability)}
                        size={40}
                      />
                    </div>
                  </td>

                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() =>
                          navigateTpProfile(
                            firmType === "1"
                              ? `my-team/details/${item.user_slug}`
                              : `resources/details/${item.user_slug}`,
                          )
                        }
                      >
                        View Profile
                      </Button>
                      {user_primary_role === "2" &&
                      (user_id === item.resource_manager_id ||
                        item.firm_id === firmId) ? (
                        <Link
                          to={
                            firmType === "1"
                              ? `my-team/edit-resource/${item.user_slug}`
                              : `resources/edit-resource/${item.user_slug}`
                          }
                        >
                          <Button variant="primary" size="sm">
                            Edit
                          </Button>
                        </Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="d-block">
      <div className="col-12 my-3">
        <div className="row">
          <div className="col-12 col-lg-5">
            <input
              type="text"
              placeholder="Search by name, location, or role"
              className="form-control text-capitalize"
              onChange={(e) => setFilterText(e.target.value)}
              value={filterText}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingPageSm title={"Loading resources..."} />
      ) : hasResult ? (
        displayList()
      ) : (
        <AlertInfo title={"Note"} message={emptyResult} />
      )}

      {availModal && (
        <AvailabilityModal
          item={availModal}
          currentUserSlug={current_user_slug}
          onClose={() => setAvailModal(null)}
          onUpdate={handleAvailabilityUpdate}
        />
      )}
    </div>
  );
};

export default ResourceListingResultBlock;
